import React, { ReactNode, useEffect, useState } from "react";
import { TopNav } from "../../../components/layouts/navbar";
import { ActiveDeals, ActiveLeads, BellRingIcon, ConversionRate, EmailIcon, FileUploadIcon, LaunchIcon, LinkedInIcon2, MessageChatbox, NewAccount, RcsIcon2, SegmentIcon, SmsIcon, TimeTaskIcon, TotalRevenue, WhatsappIcon2 } from "../../../assets/icon";
import useRequest from "../../../components/hook/use-req";
import { UserResponse } from "../../../components/types";
import { LeadQrSection } from "./lead-qr-section";
import { ProfileCard, JourneyCard } from "./profile-journey-cards";
import ConversationAgentHome from "./conversation-agent-home";
import { isConversationAgent } from "../../../utils/org-role";
// ─── Types ────────────────────────────────────────────────────────────────────

interface Channel {
  name: string;
 icon: ReactNode;
  count: number;
  description: string;
  color: string;
}

interface Stat {
  label: string;
  value: string;
  sub: string;
  icon: ReactNode;
}

interface Task {
  label: string;
  time: string;
}

interface Lead {
  name: string;
  channel: string;
  channelColor: string;
}

interface MarketingCard {
  icon: ReactNode;
  title: string;
  description: string;
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const channels: Channel[] = [
  {
    name: "WhatsApp",
    icon: <WhatsappIcon2 />,
    count: 1,
    description: "Convert inbound enquiries into qualified leads and booked jobs.",
    color: "text-[#3A3428] dark:text-gray-100",
  },
  {
    name: "SMS",
    icon: <SmsIcon />,
    count: 0,
    description: "Trigger reminders, status updates, and no-show prevention workflows on SMS.",
    color: "text-gray-500 dark:text-gray-400",
  },
  {
    name: "Email",
    icon: <EmailIcon />,
    count: 0,
    description: "Manage detailed communication, quotes, and documentation.",
    color: "text-gray-500 dark:text-gray-400",
  },
  {
    name: "RCS",
    icon: <RcsIcon2 />,
    count: 0,
    description: "Drive guided actions with rich messages, confirmations, and quick replies.",
    color: "text-gray-500 dark:text-gray-400",
  },
  {
    name: "LinkedIn",
    icon: <LinkedInIcon2 />,
    count: 0,
    description: "Capture prospects on LinkedIn and route them directly into your sales pipeline.",
    color: "text-gray-500 dark:text-gray-400",
  },
];

const stats: Stat[] = [
  { label: "Total Revenue", value: "$0", sub: "Total Revenue Generated", icon: <TotalRevenue/> },
  { label: "Active Deals", value: "0", sub: "Deals that are currently active", icon: <ActiveDeals/> },
  { label: "New Contacts", value: "0", sub: "New conversations and potential leads", icon: <NewAccount/> },
  { label: "Conversion Rate", value: "0%", sub: "How much your organisation is converting – campaign CTR too", icon: <ConversionRate/> },
];

const tasks: Task[] = [
  { label: "Email sent to Sarah", time: "2 minutes ago" },
  { label: "Call scheduled with Michael Chen", time: "9 minutes ago" },
  { label: "Call scheduled with Elon Musk", time: "10 minutes ago" },
  { label: "Deal moved to negotiation", time: "12 minutes ago" },
];

const leads: Lead[] = [
  { name: "John Doe", channel: "WhatsApp", channelColor: "text-blue-700 dark:text-blue-400" },
  { name: "David Miller", channel: "SMS", channelColor: "text-blue-700 dark:text-blue-400" },
  { name: "Sarah Connor", channel: "LinkedIn", channelColor: "text-blue-700 dark:text-blue-400" },
  { name: "Ajay Kumar", channel: "Voice", channelColor: "text-blue-700 dark:text-blue-400" },
];

const marketingCards: MarketingCard[] = [
  {
    icon: <FileUploadIcon />,
    title: "Upload Customer Data",
    description: "Upload your audience and lay the foundation for smarter outreach.",
  },
  {
    icon: <SegmentIcon />,
    title: "Create Segments",
    description: "Build dynamic audiences that update as your customers do.",
  },
  {
    icon: <LaunchIcon />,
    title: "Launch Campaigns",
    description: "Design and send campaigns that reach the right people at the right time.",
  },
];

// ─── Sub-components ───────────────────────────────────────────────────────────


const ChannelCard: React.FC<{ channel: Channel }> = ({ channel }) => (
  <div className="bg-white dark:bg-gray-900 rounded-2xl p-4 shadow-sm border border-gray-100 dark:border-gray-800 flex flex-col gap-2 min-w-[160px]">
    <div className="flex items-center justify-between">
      <span className={`text-sm flex  font-semibold ${channel.color}`}>
        {channel.icon} {channel.name}
      </span>
      <span className={`text-xs font-bold border dark:border-gray-700 px-2 py-0.5 rounded-lg bg-[#F3F4F6] dark:bg-gray-800 text-[#374151] dark:text-gray-200`}>
        {channel.count}
      </span>
    </div>
    <p className="text-[12px] text-[#7B7B7B] font-semibold dark:text-gray-500 leading-relaxed">{channel.description}</p>
    <button className="text-xs text-[#4338CA] dark:text-indigo-400 underline text-left font-semibold mt-auto">
      Connect Account
    </button>
  </div>
);

const StatCard: React.FC<{ stat: Stat }> = ({ stat }) => (
  <div className="bg-white dark:bg-gray-900 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-800">
    <div className="flex items-center gap-2 mb-2 text-gray-500 dark:text-gray-400 text-xs font-medium">
      <span>{stat.icon}</span>
      <span className="text-[#3A3428] dark:text-gray-100 font-medium text-[16px]">{stat.label}</span>
    </div>
    <p className="text-[20px] font-semibold text-[#434343] dark:text-gray-300">{stat.value}</p>
    <p className="text-[12px] text-[#434343] dark:text-gray-300 mt-1 leading-relaxed">{stat.sub}</p>
  </div>
);

const QuickActionCard: React.FC<{ icon: ReactNode; title: string; count: number; description: string }> = ({
  icon, title, count, description,
}) => (
  <div className="bg-white dark:bg-gray-900 rounded-2xl p-4 shadow-sm border border-gray-100 dark:border-gray-800">
    <div className="flex items-center justify-between mb-1">
      <span className="text-[16px] flex gap-2 font-medium text-[#3A3428] dark:text-gray-100">{icon} {title}</span>
      <span className="text-[14px] bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 p-1 rounded-lg font-bold">{count}</span>
    </div>
    <p className="text-[12px] text-[#7B7B7B] font-semibold dark:text-gray-500 leading-relaxed">{description}</p>
  </div>
);

const TaskItem: React.FC<{ task: Task }> = ({ task }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-gray-50 dark:border-gray-800 last:border-0">
    <div className="flex items-center gap-2">
      <BellRingIcon />
      <span className="text-[14px] text-[#7B7B7B] font-semibold dark:text-gray-500">{task.label}</span>
    </div>
    <span className="text-[12px] text-[#7B7B7B] font-semibold dark:text-gray-500 whitespace-nowrap ml-2">{task.time}</span>
  </div>
);

const LeadItem: React.FC<{ lead: Lead }> = ({ lead }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-gray-50 dark:border-gray-800 last:border-0">
    <div className="flex items-center gap-2">
<BellRingIcon />
      <span className="text-[14px] text-[#434343] dark:text-gray-300">{lead.name}</span>
    </div>
    <button className={`text-[14px] font-semibold underline ${lead.channelColor}`}>
      {lead.channel}
    </button>
  </div>
);

const MarketingUpgradeCard: React.FC<{ card: MarketingCard }> = ({ card }) => (
  <div className="bg-white dark:bg-gray-900 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-800 flex flex-col gap-2">
    <h3 className="flex gap-2 items-center text-[16px] font-medium text-[#3A3428] dark:text-gray-100">{card.icon} {card.title}</h3>
    <p className="text-[12px] font-semibold text-[#7B7B7B] dark:text-gray-500 leading-relaxed flex-1">{card.description}</p>
    <button className="text-[14px] text-[#4338CA] dark:text-indigo-400 underline text-left font-semibold mt-1">Upgrade Plan</button>
  </div>
);

// ─── Main Dashboard ───────────────────────────────────────────────────────────

const CrmDashboard: React.FC = () => {
    const token = localStorage.getItem("authToken");
      const [user, setUser] = useState<UserResponse | null>(null);
    const { makeRequest: getUserData } = useRequest(`/auth/get_userdata`, "GET", {
      Authorization: `Bearer ${token}`,
    });

      useEffect(() => {
        const fetchData = async () => {
          const [response] = await getUserData();
          setUser(response);

        };
        fetchData();
      }, []);

  // conversation_agent gets a stripped-down dashboard (no QR-lead-capture /
  // org-setup widgets) backed by its own aggregator endpoint — see
  // conversation-agent-home.tsx. It's fully self-contained (fetches its own
  // data), so no props are threaded through here.
  if (isConversationAgent(user?.payload?.org_role)) {
    return <ConversationAgentHome />;
  }

  return (
    <>
      <div className="min-h-screen">

        <div className="mx-auto space-y-6">
          <h1 className="text-[24px] text-gray-900 dark:text-gray-100">
            Hi, <span className="font-bold">{user?.payload?.first_name} {user?.payload?.last_name}</span>
          </h1>
          <p className="text-[14px] font-semibold mb-4 text-gray-700 dark:text-gray-300">Let's pick up where you left off and keep things moving.</p>
          {/* Header Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ProfileCard user={user} />
            <JourneyCard />
          </div>

          <LeadQrSection user={user} />

          {/* Communication Channels */}
          <section>
            <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-3">Your Active Communication Channels</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {channels.map((ch) => (
                <ChannelCard key={ch.name} channel={ch} />
              ))}
            </div>
          </section>

          {/* Stats */}
          <section>
            <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-3">Current Numbers at a Glance</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {stats.map((s) => (
                <StatCard key={s.label} stat={s} />
              ))}
            </div>
          </section>

          {/* Quick Actions */}
          <section>
            <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-3">Keep up with conversations, tasks, and leads</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <QuickActionCard icon={<MessageChatbox />} title="Conversations" count={0} description="Stay on top of every customer interaction from one unified inbox." />
              <QuickActionCard icon={<ConversionRate />} title="Your Tasks" count={5} description="Plan, track, and complete your daily to-dos with clarity." />
              <QuickActionCard icon={<ActiveLeads />} title="Active Leads" count={0} description="Identify high-intent prospects and engage them at the right moment." />
            </div>
          </section>

          {/* Tasks & Leads */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Recent Tasks */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-800">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-[16px] flex gap-2 font-semibold text-[#3A3428] dark:text-gray-100"><TimeTaskIcon /> Recent Tasks</h3>
                <span className="text-[14px] bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 p-2 rounded-lg font-bold">5</span>
              </div>
              {tasks.map((t) => (
                <TaskItem key={t.label} task={t} />
              ))}
            </div>

            {/* Leads to Pursue */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-800">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-[16px] flex gap-2 font-semibold text-[#3A3428] dark:text-gray-100"><ActiveLeads /> Leads to Pursue</h3>
                <span className="text-[14px] bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 p-2 rounded-lg font-bold">4</span>
              </div>
              {leads.map((l) => (
                <LeadItem key={l.name} lead={l} />
              ))}
            </div>
          </div>

          {/* Marketing Platform */}
          <section>
            <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-4">Do more with ConvoHub Marketing Platform</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {marketingCards.map((card) => (
                <MarketingUpgradeCard key={card.title} card={card} />
              ))}
            </div>
          </section>

        </div>
      </div>
    </>
  );
};

export default CrmDashboard;